import { useQuery, UseQueryResult } from '@tanstack/react-query';
import CategoryApiV2 from '../../../lib/api/category/CategoryApiV2';
import { CATEGORY } from '../../../constants/queryKeys';
import { PRODUCT_CATEGORY_LINK_BULK_JOB_STALE_TIME_MS } from '../../../constants/service/app/productCategoryLinkBulkConstants';
import {
  isBulkJobPollingDone,
  resolveBulkJobPollInterval,
} from '../../../lib/utils/productCategoryLinkBulkUtils';
import { BulkUploadJobQueryData } from '../../../types/category/bulkUpload';

type UseProductCategoryLinkBulkJobQueryParams = {
  jobId: string | null;
  /** 폴링 간격 산출 기준 시각 (epoch ms) */
  startedAt: number;
  /**
   * 폴링 여부. 종결 후 false 로 내리면 창 포커스 refetch 까지 함께 멈춘다.
   * jobId 를 null 로 바꾸는 대신 이 값으로 끊어야 쿼리 키가 유지돼 마지막 응답이 캐시에 남는다.
   */
  enabled?: boolean;
};

/**
 * 벌크 잡 상태 폴링.
 *
 * 간격은 경과 시간에 따라 넓어진다(2초 → 5초 → 15초). refetchInterval 콜백은 응답을
 * 받을 때마다 다시 계산되므로, 구간 전환은 정확히 30초/2분 시점이 아니라 그 이후
 * 첫 응답에서 일어난다.
 */
export const useProductCategoryLinkBulkJobQuery = ({
  jobId,
  startedAt,
  enabled = true,
}: UseProductCategoryLinkBulkJobQueryParams): UseQueryResult<BulkUploadJobQueryData, unknown> =>
  useQuery(
    [...CATEGORY.LIST_EXCEL_BULK_JOB, jobId],
    () => CategoryApiV2.getProductCategoryLinkBulkJob(jobId as string),
    {
      enabled: !!jobId && enabled,
      // 메인 잡 완료 후에도 ES 동기화가 진행 중이면 폴링을 이어간다 (완료 카드의 수치 갱신)
      refetchInterval: (data) => (isBulkJobPollingDone(data) ? false : resolveBulkJobPollInterval(Date.now() - startedAt)),
      // 전역 기본값은 retry: 0 이라 일시적인 네트워크 오류 한 번으로 폴링이 죽는다
      retry: 2,
      // 탭이 백그라운드면 폴링이 멈추므로(refetchIntervalInBackground 기본 false)
      // 복귀 시 즉시 최신 상태를 받는다
      refetchOnWindowFocus: true,
      // alt-tab 을 반복할 때 포커스 refetch 가 매번 나가지 않도록 짧게 dedupe
      staleTime: PRODUCT_CATEGORY_LINK_BULK_JOB_STALE_TIME_MS,
    },
  );
